import { Check, Minus, X } from 'lucide-react';

import { cn } from '../lib/utils';

export type StepState = 'complete' | 'current' | 'upcoming' | 'skipped' | 'rejected';

export interface Step {
  id: string;
  label: string;
  state: StepState;
}

interface StepperProps {
  steps: Step[];
  /** Names the stepper for assistive technology, e.g. "Onboarding progress". */
  label: string;
  className?: string;
}

const STATE_LABELS: Record<StepState, string> = {
  complete: 'done',
  current: 'current',
  upcoming: 'upcoming',
  skipped: 'skipped',
  rejected: 'needs changes',
};

const MARKER_CLASSES: Record<StepState, string> = {
  complete: 'border-success bg-success text-primary-foreground',
  current: 'border-primary bg-primary text-primary-foreground ring-4 ring-primary/20',
  upcoming: 'border-border bg-card text-muted-foreground',
  skipped: 'border-dashed border-muted-foreground bg-muted text-muted-foreground',
  rejected: 'border-destructive bg-destructive text-primary-foreground',
};

const LABEL_CLASSES: Record<StepState, string> = {
  complete: 'text-foreground',
  current: 'font-semibold text-foreground',
  upcoming: 'text-muted-foreground',
  skipped: 'text-muted-foreground line-through',
  rejected: 'font-medium text-destructive',
};

function StepMarker({ step, position }: { step: Step; position: number }) {
  if (step.state === 'complete') return <Check className="size-3.5" aria-hidden />;
  if (step.state === 'rejected') return <X className="size-3.5" aria-hidden />;
  if (step.state === 'skipped') return <Minus className="size-3.5" aria-hidden />;

  return <span aria-hidden>{position}</span>;
}

/** The step to name on a narrow screen: the current one, else the first that needs changes, else the last done. */
const focusIndex = (steps: Step[]): number => {
  const current = steps.findIndex((step) => step.state === 'current');

  if (current >= 0) return current;

  const rejected = steps.findIndex((step) => step.state === 'rejected');

  if (rejected >= 0) return rejected;

  const lastDone = steps.map((step) => step.state).lastIndexOf('complete');

  return Math.max(lastDone, 0);
};

/**
 * A horizontal progress line. It scrolls inside its own frame when the steps are wider than
 * the screen, and below `sm` shows one "Step n of m" line instead, so the window never scrolls.
 */
export function Stepper({ steps, label, className }: StepperProps) {
  if (steps.length === 0) return null;

  const focus = focusIndex(steps);
  const focused = steps[focus];

  return (
    <nav aria-label={label} className={cn('min-w-0', className)}>
      <p className="text-sm text-foreground sm:hidden" data-testid="stepper-compact">
        <span className="font-semibold">
          Step {focus + 1} of {steps.length}
        </span>
        {focused ? <span className="text-muted-foreground"> — {focused.label}</span> : null}
      </p>

      <ol
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must take focus so a keyboard can scroll it
        tabIndex={0}
        aria-label={`${label}, scrollable`}
        className="hidden min-w-0 items-start gap-0 overflow-x-auto pb-1 sm:flex"
      >
        {steps.map((step, index) => (
          <li
            key={step.id}
            aria-current={step.state === 'current' ? 'step' : undefined}
            data-state={step.state}
            className="flex min-w-24 flex-1 flex-col items-center gap-1.5 px-1 text-center"
          >
            <div className="flex w-full items-center">
              <span
                aria-hidden
                className={cn(
                  'h-0.5 flex-1',
                  index === 0 ? 'bg-transparent' : 'bg-border',
                  steps[index - 1]?.state === 'complete' && 'bg-success',
                )}
              />
              <span
                className={cn(
                  'flex size-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold',
                  MARKER_CLASSES[step.state],
                )}
              >
                <StepMarker step={step} position={index + 1} />
              </span>
              <span
                aria-hidden
                className={cn(
                  'h-0.5 flex-1',
                  index === steps.length - 1 ? 'bg-transparent' : 'bg-border',
                  step.state === 'complete' && index < steps.length - 1 && 'bg-success',
                )}
              />
            </div>
            <span
              className={cn('text-xs leading-tight', LABEL_CLASSES[step.state])}
              title={step.label}
            >
              {step.label}
              {step.state === 'current' ? null : (
                <span className="sr-only"> ({STATE_LABELS[step.state]})</span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}
